import { onBeforeUnmount, ref, watch } from "vue";
import { useMediaQuery } from "../composables/useSlideshow";
import type { Rotation } from "./use-rotation";

/** Phones and tablets, where the bottom bar shows too (`global/isMobile || isTablet`). */
export const TRACK_QUERY = "(max-width: 991px)";

/** Movement under this many pixels is a tap; sideways beyond it, a swipe. */
const TAP_PX = 10;
/** Scroll events stop this long before a swipe counts as settled. */
const SETTLE_MS = 120;

/**
 * On phones and tablets the cards sit in a sideways track that swipes. Rotation scrolls it,
 * and the first tap or sideways swipe stops rotation for good (WCAG 2.2.2); scrolling the
 * page up or down over the cards doesn't. Once stopped, swiping picks the active card.
 *
 * Bind `track` to the scrolling row and `chips` to the chip row; it listens on them and on
 * the rotation's block itself.
 */
export function useSwipeTrack(rotation: Rotation) {
  const active = useMediaQuery(TRACK_QUERY);
  const track = ref<HTMLElement | null>(null);
  const chips = ref<HTMLElement | null>(null);

  let start: { x: number; y: number } | null = null;
  let moved = false;
  // The track already shows this card: the visitor swiped there.
  let swipedTo = -1;
  let settle: ReturnType<typeof setTimeout> | undefined;

  const behavior = (): ScrollBehavior =>
    rotation.reducedMotion.value ? "auto" : "smooth";

  const slides = () => [...(track.value?.children ?? [])] as HTMLElement[];

  /** How far the row must scroll to bring the item to its start. */
  const distance = (row: HTMLElement, item: HTMLElement) =>
    item.getBoundingClientRect().left -
    row.getBoundingClientRect().left -
    (Number.parseFloat(getComputedStyle(row).scrollPaddingLeft) || 0);

  const scrollTo = (row: HTMLElement, item: HTMLElement) =>
    row.scrollTo({
      behavior: behavior(),
      left: row.scrollLeft + distance(row, item),
    });

  /** The card at the track's start; at the far end, the last one. */
  function shownIndex(): number {
    const row = track.value;
    const items = slides();
    if (!(row && items.length)) {
      return 0;
    }
    if (row.scrollLeft >= row.scrollWidth - row.clientWidth - 2) {
      return items.length - 1;
    }
    const gaps = items.map((item) => Math.abs(distance(row, item)));
    return gaps.indexOf(Math.min(...gaps));
  }

  function stop() {
    if (active.value) {
      rotation.stop();
    }
  }

  // The active chip slides to the row's start while chips are hidden beyond it.
  function showChip(i: number) {
    const row = chips.value;
    const chip = row?.children[i] as HTMLElement | undefined;
    if (row && chip && row.scrollWidth > row.clientWidth) {
      scrollTo(row, chip);
    }
  }

  watch(rotation.index, (i) => {
    if (!active.value) {
      return;
    }
    showChip(i);
    const row = track.value;
    const item = slides()[i];
    if (row && item && i !== swipedTo) {
      scrollTo(row, item);
    }
    swipedTo = -1;
  });

  function onScroll() {
    clearTimeout(settle);
    // While rotating, the track scrolls because of the rotation, not the visitor.
    if (!active.value || rotation.rotates.value) {
      return;
    }
    settle = setTimeout(() => {
      const i = shownIndex();
      if (i !== rotation.index.value) {
        swipedTo = i;
        rotation.go(i);
      }
    }, SETTLE_MS);
  }

  function onTouchStart(e: TouchEvent) {
    const [touch] = e.touches;
    start = touch ? { x: touch.clientX, y: touch.clientY } : null;
    moved = false;
  }

  function onTouchMove(e: TouchEvent) {
    const [touch] = e.touches;
    if (!(start && touch)) {
      return;
    }
    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (dx > TAP_PX || dy > TAP_PX) {
      moved = true;
    }
    if (dx > TAP_PX && dx > dy) {
      stop();
    }
  }

  function onTouchEnd() {
    if (start && !moved) {
      stop();
    }
    start = null;
  }

  function onWheel(e: WheelEvent) {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      stop();
    }
  }

  // A click is a tap with a mouse, or a chip or button from the keyboard.
  const blockListeners: [string, EventListener][] = [
    ["click", stop],
    ["touchend", onTouchEnd],
    ["touchmove", onTouchMove as EventListener],
    ["touchstart", onTouchStart as EventListener],
    ["wheel", onWheel as EventListener],
  ];

  function listen(
    pairs: [string, EventListener][],
    node: HTMLElement | null,
    old?: HTMLElement | null
  ) {
    for (const [type, fn] of pairs) {
      old?.removeEventListener(type, fn);
      node?.addEventListener(type, fn, { passive: true });
    }
  }

  // Post flush: after the template refs are set, also on the first render.
  watch(rotation.el, (node, old) => listen(blockListeners, node, old), {
    flush: "post",
  });
  watch(track, (node, old) => listen([["scroll", onScroll]], node, old), {
    flush: "post",
  });

  onBeforeUnmount(() => {
    clearTimeout(settle);
    listen(blockListeners, null, rotation.el.value);
    listen([["scroll", onScroll]], null, track.value);
  });

  return { active, chips, track };
}

export type SwipeTrack = ReturnType<typeof useSwipeTrack>;
